import { createContext, useContext, useState, useMemo, ReactNode, useEffect, useRef } from 'react';
import { useShopTimezone } from './TimezoneContext';
import { useAuth } from './AuthContext';
import { getShopDayLabel } from '@/utils/shopTime';
import { clock } from '@/utils/clock';

// DCD-A5: workstream final closure. The deprecated `setSelectedDate`
// API has been removed from DateContextType now that consumer count
// is zero (DCD-A4 verified). Shop-time is the SOLE authority for
// shop-day selection — every consumer routes through the canonical
// `setSelectedDayLabel` setter, which accepts shop-tz-canonical
// YYYY-MM-DD strings. Browser-local `Date()` no longer drives any
// DateContext write path. Migration arc: DCD-A1 (audit + dead
// destructure) → DCD-A2 (TopBar go-to-today) → DCD-A3 (format cohort)
// → DCD-A4 (ImportJobs retirement, consumer count → 0) → DCD-A5
// (this round, type cleanup).
interface DateContextType {
  /**
   * SOURCE OF TRUTH: Shop-local day label in YYYY-MM-DD format
   * This is what should be used for ALL database queries
   */
  selectedDayLabel: string;

  /**
   * DERIVED: Date object for UI components (calendar display)
   * Created from selectedDayLabel at noon to avoid boundary issues
   * Do NOT use this for database queries
   */
  selectedDate: Date;

  /**
   * Set the selected day directly using a YYYY-MM-DD string.
   * This is the SOLE setter — the deprecated `setSelectedDate(date: Date)`
   * was removed in DCD-A5 once every consumer migrated to this canonical
   * shop-time-authoritative API.
   */
  setSelectedDayLabel: (label: string) => void;
}

const DateContext = createContext<DateContextType | undefined>(undefined);

export const DateProvider = ({ children }: { children: ReactNode }) => {
  const { shopTimezone, isLoading } = useShopTimezone();
  
  // Initialize with today's date in shop timezone
  const [selectedDayLabel, setSelectedDayLabel] = useState(() => {
    // On initial load, use browser timezone as temporary fallback
    // Will be corrected once shopTimezone loads.
    //
    // Chronomancer R-PAST-14: route through `clock.now()` so tests that
    // freeze the clock (e.g. to simulate navigating at midnight) see a
    // stable initial day label. Previously used raw `new Date()` which
    // ignored the clock abstraction for this central UI state.
    return getShopDayLabel(clock.now().toISOString(), shopTimezone);
  });

  // Re-compute today's label when timezone loads
  useEffect(() => {
    if (!isLoading && shopTimezone) {
      // Re-validate current selection with loaded timezone
      const todayInShopTz = getShopDayLabel(clock.now().toISOString(), shopTimezone);
      // Only update if we're still on the initial date (today)
      // Don't reset if user has navigated to a different date
      setSelectedDayLabel(prev => {
        // If the previous value was today in any timezone, update to shop timezone today
        const prevDate = new Date(prev + 'T12:00:00');
        const today = clock.now();
        if (
          prevDate.getFullYear() === today.getFullYear() &&
          prevDate.getMonth() === today.getMonth() &&
          prevDate.getDate() === today.getDate()
        ) {
          return todayInShopTz;
        }
        return prev;
      });
    }
  }, [shopTimezone, isLoading]);
  
  // Each NEW signed-in session starts on shop-local today. The provider sits
  // above the router and outlives sign-out, so without this a date browsed in
  // one session (or before an inactivity sign-out) silently became the next
  // session's day. Never reset during a session: token refreshes keep the id.
  const { user } = useAuth();
  const sessionUserRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const id = user?.id ?? null;
    const prev = sessionUserRef.current;
    sessionUserRef.current = id;
    if (prev === undefined || id === null || id === prev) return;
    setSelectedDayLabel(getShopDayLabel(clock.now().toISOString(), shopTimezone));
  // shopTimezone is read, not followed: the timezone effect above corrects today
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Derive Date object for UI convenience (noon in local time for calendar display)
  // This avoids DST/boundary issues when displaying in calendar components
  const selectedDate = useMemo(() => {
    const [year, month, day] = selectedDayLabel.split('-').map(Number);
    // Create at noon to avoid any timezone boundary issues in UI
    return new Date(year, month - 1, day, 12, 0, 0);
  }, [selectedDayLabel]);

  return (
    <DateContext.Provider value={{
      selectedDayLabel,
      selectedDate,
      setSelectedDayLabel,
    }}>
      {children}
    </DateContext.Provider>
  );
};

export const useDate = () => {
  const context = useContext(DateContext);
  if (!context) {
    throw new Error('useDate must be used within a DateProvider');
  }
  return context;
};
